import type { ThreadSummary } from "@shared/types";

/**
 * Sidebar-session helpers for an in-flight (not-yet-persisted) chat.
 *
 * A brand-new session is only mirrored into Postgres at `onDone` (server
 * chat.ts), so the sidebar list (`GET /api/sessions`) does not contain it while
 * the answer is still streaming. If the user navigates to a previous thread
 * mid-stream, the new chat would vanish — gone from the view AND absent from the
 * left nav (#225, #175). To keep a started chat always reachable, the client
 * pins an optimistic summary into the sidebar from `onStart` until the server
 * list catches up.
 */

/**
 * Derive a sidebar title from the first user message. Mirrors the server's
 * title rule in chat.ts so the optimistic entry reads the same as the persisted
 * one (no flicker when the real list arrives).
 */
export function deriveTitle(message: string): string {
  const base = message.trim().length > 0 ? message.trim() : "[图片]";
  return base.length > 30 ? `${base.slice(0, 30)}…` : base;
}

/**
 * Merge the server's session list with an in-flight session that may not be
 * persisted yet. The in-flight entry is pinned to the top until the server list
 * already includes it (after the turn finishes and is mirrored), at which point
 * the fetched list is returned untouched — no duplicate, no flicker.
 */
export function mergeInflight(
  fetched: ThreadSummary[],
  inflight: ThreadSummary | null
): ThreadSummary[] {
  if (inflight === null) return fetched;
  if (fetched.some((t) => t.id === inflight.id)) return fetched;
  return [inflight, ...fetched];
}
